import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Input,
  Spinner,
  Stack,
  Switch,
  Table,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { FiArrowLeft, FiRefreshCw, FiShield, FiTrash2 } from "react-icons/fi"

import { OpenAPI } from "@/client"
import { IconBox, VisionButton, VisionCard } from "@/components/VisionUI"
import useCustomToast from "@/hooks/useCustomToast"
import { useSemanticColors } from "@/hooks/useSemanticColors"

type IpBanRead = {
  id: string
  ip_address: string
  banned_until: string
  reason: string | null
  created_at: string
  created_by: string | null
}

type IpBanSettings = {
  enabled: boolean
  attempts_threshold: number
  window_seconds: number
  ban_seconds: number
}

type Enforcement = "off" | "optional" | "required_superusers" | "required_all"
type TwoSvSettings = { enforcement: Enforcement }
type TwoSvUserRow = {
  id: string
  email: string
  full_name: string | null
  is_superuser: boolean
  totp_enabled: boolean
  confirmed_at: string | null
}

// Pull the backend's `detail` out of a failed response so the toast explains
// *why* a save was rejected (e.g. permission / demo-mode) instead of a generic
// "failed to save".
async function errorDetail(r: Response, fallback: string): Promise<string> {
  try {
    const body = await r.json()
    if (typeof body?.detail === "string") return body.detail
  } catch {
    // non-JSON body — fall through
  }
  return `${fallback} (${r.status})`
}

function apiFetch(path: string, init: RequestInit = {}) {
  const token = localStorage.getItem("access_token")
  const base = (OpenAPI.BASE || "").replace(/\/$/, "")
  return fetch(`${base}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...(init.headers || {}),
    },
  })
}

export const Route = createFileRoute("/_layout/admin-ip-bans")({
  component: AdminIpBans,
})

function AdminIpBans() {
  const sc = useSemanticColors()
  const { t } = useTranslation("ipBans")
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { showToast } = useCustomToast()

  const settingsQuery = useQuery({
    queryKey: ["ip-ban-settings"],
    queryFn: () =>
      apiFetch("/api/v1/admin/ip-bans/settings").then((r) => {
        if (!r.ok) throw new Error("Failed to load settings")
        return r.json() as Promise<IpBanSettings>
      }),
    refetchInterval: 60_000,
  })

  const bansQuery = useQuery({
    queryKey: ["ip-bans"],
    queryFn: () =>
      apiFetch("/api/v1/admin/ip-bans").then((r) => {
        if (!r.ok) throw new Error("Failed to load bans")
        return r.json() as Promise<{ data: IpBanRead[]; count: number }>
      }),
    refetchInterval: 30_000,
  })

  // Local form state, seeded from the settings query.
  const [enabled, setEnabled] = useState(true)
  const [attemptsThreshold, setAttemptsThreshold] = useState(5)
  const [windowSeconds, setWindowSeconds] = useState(300)
  const [banSeconds, setBanSeconds] = useState(900)

  useEffect(() => {
    if (settingsQuery.data) {
      setEnabled(settingsQuery.data.enabled)
      setAttemptsThreshold(settingsQuery.data.attempts_threshold)
      setWindowSeconds(settingsQuery.data.window_seconds)
      setBanSeconds(settingsQuery.data.ban_seconds)
    }
  }, [settingsQuery.data])

  const saveSettings = useMutation({
    mutationFn: (body: IpBanSettings) =>
      apiFetch("/api/v1/admin/ip-bans/settings", {
        method: "PUT",
        body: JSON.stringify(body),
      }).then(async (r) => {
        if (!r.ok)
          throw new Error(await errorDetail(r, "Failed to save settings"))
        return r.json()
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ip-ban-settings"] })
      showToast(t("toast.savedTitle"), t("toast.savedBody"), "success")
    },
    onError: (err) =>
      showToast(
        t("toast.saveErrorTitle"),
        err instanceof Error ? err.message : String(err),
        "error",
      ),
  })

  const unban = useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/api/v1/admin/ip-bans/${id}`, { method: "DELETE" }).then(
        (r) => {
          if (!r.ok) throw new Error("Failed to unban")
          return r.json()
        },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ip-bans"] })
      showToast(t("toast.unbannedTitle"), "", "success")
    },
    onError: (err) =>
      showToast(
        t("toast.unbanErrorTitle"),
        err instanceof Error ? err.message : String(err),
        "error",
      ),
  })

  const dirty =
    settingsQuery.data &&
    (enabled !== settingsQuery.data.enabled ||
      attemptsThreshold !== settingsQuery.data.attempts_threshold ||
      windowSeconds !== settingsQuery.data.window_seconds ||
      banSeconds !== settingsQuery.data.ban_seconds)

  // ---- Two-step verification: policy + admin user view ----
  const twoSvSettingsQuery = useQuery({
    queryKey: ["2sv-settings"],
    queryFn: () =>
      apiFetch("/api/v1/admin/2sv/settings").then((r) => {
        if (!r.ok) throw new Error("Failed to load 2SV settings")
        return r.json() as Promise<TwoSvSettings>
      }),
  })

  const twoSvUsersQuery = useQuery({
    queryKey: ["2sv-users"],
    queryFn: () =>
      apiFetch("/api/v1/admin/2sv/users").then((r) => {
        if (!r.ok) throw new Error("Failed to load users")
        return r.json() as Promise<{ data: TwoSvUserRow[]; count: number }>
      }),
    refetchInterval: 60_000,
  })

  const [enforcement, setEnforcement] = useState<Enforcement>("optional")

  useEffect(() => {
    if (twoSvSettingsQuery.data) {
      setEnforcement(twoSvSettingsQuery.data.enforcement)
    }
  }, [twoSvSettingsQuery.data])

  const saveTwoSv = useMutation({
    mutationFn: (body: TwoSvSettings) =>
      apiFetch("/api/v1/admin/2sv/settings", {
        method: "PUT",
        body: JSON.stringify(body),
      }).then(async (r) => {
        if (!r.ok)
          throw new Error(await errorDetail(r, "Failed to save 2SV settings"))
        return r.json()
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["2sv-settings"] })
      showToast(t("toast.savedTitle"), t("toast.savedBody"), "success")
    },
    onError: (err) =>
      showToast(
        t("toast.saveErrorTitle"),
        err instanceof Error ? err.message : String(err),
        "error",
      ),
  })

  const resetUserTwoSv = useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/api/v1/admin/2sv/users/${id}/reset`, { method: "POST" }).then(
        (r) => {
          if (!r.ok) throw new Error("Failed to reset")
          return r.json()
        },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["2sv-users"] })
      showToast(
        t("twoSv.resetToast", "Two-step verification reset"),
        "",
        "success",
      )
    },
    onError: (err) =>
      showToast(
        t("toast.saveErrorTitle"),
        err instanceof Error ? err.message : String(err),
        "error",
      ),
  })

  const twoSvDirty =
    twoSvSettingsQuery.data &&
    enforcement !== twoSvSettingsQuery.data.enforcement

  return (
    <Box>
      <Flex mb={4} align="center" gap={3}>
        <VisionButton
          variant="ghost"
          size="sm"
          onClick={() => navigate({ to: "/admin" })}
        >
          <FiArrowLeft />
          <Text ml={2}>{t("common.backToAdmin")}</Text>
        </VisionButton>
      </Flex>

      <Flex align="center" gap={3} mb={6} flexWrap="wrap">
        <IconBox variant="glass" size="lg">
          <FiShield fontSize="24px" color={sc.iconColor} />
        </IconBox>
        <Box flex="1">
          <Heading size="lg" color={sc.heading}>
            {t("title")}
          </Heading>
          <Text color={sc.textMuted}>{t("subtitle")}</Text>
        </Box>
      </Flex>

      {/* Settings card */}
      <VisionCard mb={6}>
        <VStack align="stretch" gap={4} p={5}>
          <Heading size="sm" color={sc.heading}>
            {t("settings.title")}
          </Heading>
          {settingsQuery.isLoading ? (
            <Flex justify="center" py={6}>
              <Spinner size="md" color={sc.spinnerColor} />
            </Flex>
          ) : (
            <Stack gap={4}>
              <Flex align="center" justify="space-between" gap={4}>
                <Box>
                  <Text fontWeight="500" color={sc.heading}>
                    {t("settings.enabledLabel")}
                  </Text>
                  <Text fontSize="xs" color={sc.textMuted}>
                    {t("settings.enabledHelp")}
                  </Text>
                </Box>
                <Switch.Root
                  checked={enabled}
                  onCheckedChange={(e) => setEnabled(e.checked)}
                  colorPalette="blue"
                  size="lg"
                >
                  <Switch.HiddenInput />
                  <Switch.Control>
                    <Switch.Thumb />
                  </Switch.Control>
                </Switch.Root>
              </Flex>

              <Flex gap={4} flexWrap="wrap">
                <Box flex="1" minW="180px">
                  <Text fontSize="xs" color={sc.textMuted} mb={1}>
                    {t("settings.attemptsLabel")}
                  </Text>
                  <Input
                    size="sm"
                    type="number"
                    value={attemptsThreshold}
                    onChange={(e) =>
                      setAttemptsThreshold(Number(e.target.value))
                    }
                  />
                  <Text fontSize="xs" color={sc.textMuted} mt={1}>
                    {t("settings.attemptsHelp")}
                  </Text>
                </Box>
                <Box flex="1" minW="180px">
                  <Text fontSize="xs" color={sc.textMuted} mb={1}>
                    {t("settings.windowLabel")}
                  </Text>
                  <Input
                    size="sm"
                    type="number"
                    value={windowSeconds}
                    onChange={(e) => setWindowSeconds(Number(e.target.value))}
                  />
                  <Text fontSize="xs" color={sc.textMuted} mt={1}>
                    {t("settings.windowHelp")}
                  </Text>
                </Box>
                <Box flex="1" minW="180px">
                  <Text fontSize="xs" color={sc.textMuted} mb={1}>
                    {t("settings.banLabel")}
                  </Text>
                  <Input
                    size="sm"
                    type="number"
                    value={banSeconds}
                    onChange={(e) => setBanSeconds(Number(e.target.value))}
                  />
                  <Text fontSize="xs" color={sc.textMuted} mt={1}>
                    {t("settings.banHelp")}
                  </Text>
                </Box>
              </Flex>

              <Flex>
                <Button
                  size="sm"
                  colorScheme="blue"
                  disabled={!dirty || saveSettings.isPending}
                  onClick={() =>
                    saveSettings.mutate({
                      enabled,
                      attempts_threshold: attemptsThreshold,
                      window_seconds: windowSeconds,
                      ban_seconds: banSeconds,
                    })
                  }
                >
                  {t("settings.saveBtn")}
                </Button>
              </Flex>
            </Stack>
          )}
        </VStack>
      </VisionCard>

      {/* Two-step verification policy */}
      <VisionCard mb={6}>
        <VStack align="stretch" gap={4} p={5}>
          <Flex align="center" justify="space-between" gap={3} flexWrap="wrap">
            <Heading size="sm" color={sc.heading}>
              {t("twoSv.title", "Two-step verification")}
            </Heading>
            {/* Enrollment is self-service; jump the admin to their own
                Settings page to set up their personal 2SV. */}
            <VisionButton
              variant="ghost"
              size="sm"
              onClick={() => navigate({ to: "/settings" })}
            >
              {t("twoSv.setUpMine", "Set up my 2SV →")}
            </VisionButton>
          </Flex>
          {twoSvSettingsQuery.isLoading ? (
            <Flex justify="center" py={6}>
              <Spinner size="md" color={sc.spinnerColor} />
            </Flex>
          ) : (
            <Stack gap={4}>
              <Box>
                <Text fontSize="xs" color={sc.textMuted} mb={2}>
                  {t("twoSv.policyLabel", "Policy")}
                </Text>
                <Flex gap={2} flexWrap="wrap">
                  {(
                    [
                      ["off", t("twoSv.off", "Off")],
                      ["optional", t("twoSv.optional", "Optional")],
                      [
                        "required_superusers",
                        t("twoSv.requiredSuperusers", "Required for admins"),
                      ],
                      [
                        "required_all",
                        t("twoSv.requiredAll", "Required for everyone"),
                      ],
                    ] as [Enforcement, string][]
                  ).map(([val, label]) => (
                    <Button
                      key={val}
                      size="sm"
                      variant={enforcement === val ? "solid" : "outline"}
                      colorScheme={enforcement === val ? "blue" : undefined}
                      onClick={() => setEnforcement(val)}
                    >
                      {label}
                    </Button>
                  ))}
                </Flex>
                <Text fontSize="xs" color={sc.textMuted} mt={2}>
                  {t(
                    "twoSv.policyHelp",
                    "Off disables 2SV for everyone. Optional lets users opt in. Required users are nudged to enroll; logins keep working until they do.",
                  )}
                </Text>
              </Box>

              <Flex>
                <Button
                  size="sm"
                  colorScheme="blue"
                  disabled={!twoSvDirty || saveTwoSv.isPending}
                  onClick={() => saveTwoSv.mutate({ enforcement })}
                >
                  {t("settings.saveBtn")}
                </Button>
              </Flex>
            </Stack>
          )}
        </VStack>
      </VisionCard>

      {/* User enrollment (admin view + reset for lockout recovery) */}
      <VisionCard mb={6}>
        <Flex justify="space-between" align="center" p={4}>
          <Heading size="md" color={sc.heading}>
            {t("twoSv.usersTitle", "User enrollment")}
          </Heading>
          <VisionButton
            variant="ghost"
            size="sm"
            onClick={() => twoSvUsersQuery.refetch()}
          >
            <FiRefreshCw />
          </VisionButton>
        </Flex>
        {twoSvUsersQuery.isLoading ? (
          <Flex justify="center" py={6}>
            <Spinner size="md" color={sc.spinnerColor} />
          </Flex>
        ) : (
          <Box overflowX="auto">
            <Table.Root size="sm">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("twoSv.colUser", "User")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("twoSv.colStatus", "2SV")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("list.col.actions")}
                  </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {(twoSvUsersQuery.data?.data ?? []).map((u) => (
                  <Table.Row key={u.id}>
                    <Table.Cell color={sc.heading} fontSize="sm">
                      {u.email}
                      {u.is_superuser ? " ⭐" : ""}
                    </Table.Cell>
                    <Table.Cell>
                      <Badge
                        colorPalette={u.totp_enabled ? "green" : "gray"}
                        size="sm"
                      >
                        {u.totp_enabled
                          ? t("twoSv.statusOn", "On")
                          : t("twoSv.statusOff", "Off")}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <Button
                        size="xs"
                        variant="outline"
                        disabled={!u.totp_enabled || resetUserTwoSv.isPending}
                        onClick={() => resetUserTwoSv.mutate(u.id)}
                      >
                        {t("twoSv.reset", "Reset")}
                      </Button>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Box>
        )}
      </VisionCard>

      {/* Active bans list */}
      <VisionCard>
        <Flex justify="space-between" align="center" p={4}>
          <Heading size="md" color={sc.heading}>
            {t("list.title", { count: bansQuery.data?.count ?? 0 })}
          </Heading>
          <VisionButton
            variant="ghost"
            size="sm"
            onClick={() => bansQuery.refetch()}
          >
            <FiRefreshCw />
          </VisionButton>
        </Flex>
        {bansQuery.isLoading ? (
          <Flex justify="center" py={6}>
            <Spinner size="md" color={sc.spinnerColor} />
          </Flex>
        ) : (bansQuery.data?.data ?? []).length === 0 ? (
          <Text color={sc.textMuted} fontSize="sm" px={4} pb={4}>
            {t("list.empty")}
          </Text>
        ) : (
          <Box overflowX="auto">
            <Table.Root size="sm">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("list.col.ip")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("list.col.bannedUntil")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("list.col.reason")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("list.col.actions")}
                  </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {(bansQuery.data?.data ?? []).map((row) => (
                  <Table.Row key={row.id}>
                    <Table.Cell
                      color={sc.heading}
                      fontSize="sm"
                      fontFamily="mono"
                    >
                      {row.ip_address}
                    </Table.Cell>
                    <Table.Cell color={sc.heading} fontSize="sm">
                      {row.banned_until.replace("T", " ").slice(0, 19)}
                    </Table.Cell>
                    <Table.Cell color={sc.textMuted} fontSize="xs">
                      {row.reason ?? "—"}
                    </Table.Cell>
                    <Table.Cell>
                      <Button
                        size="xs"
                        colorScheme="red"
                        variant="outline"
                        disabled={unban.isPending}
                        onClick={() => unban.mutate(row.id)}
                      >
                        <FiTrash2 style={{ marginRight: 6 }} />
                        {t("list.unbanBtn")}
                      </Button>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Box>
        )}
      </VisionCard>
    </Box>
  )
}
